import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { maestrosDataService } from "@/services/maestros/maestros-data.service";

interface MasterData {
  [key: string]: any;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

type ServiceConfig = {
  [key: string]: any;
};

const serviceMap: Record<
  string,
  (config?: any, signal?: AbortSignal) => Promise<any>
> = {
  // Servicios que usan filtros
  sociedades: (config, signal) =>
    maestrosDataService.getSociedades({
      filters: config?.filters ?? {},
      signal,
    }),
  productos: (config, signal) =>
    maestrosDataService.getProductos({
      filters: config?.filters ?? {},
      signal,
    }),
  laboratorios: (config, signal) =>
    maestrosDataService.getLaboratorios({
      filters: config?.filters ?? {},
      signal,
    }),
  aprobadores: (config, signal) =>
    maestrosDataService.getAprobadores({
      filters: config?.filters ?? {},
      signal,
    }),

  // Servicios que no usan filtros
  grupos: (_, signal) => maestrosDataService.getGrupos({ signal }),
  escalas: (_, signal) => maestrosDataService.getEscalas({ signal }),
  descuentos: (_, signal) => maestrosDataService.getDescuento({ signal }),
  tipo_descuentos: (_, signal) =>
    maestrosDataService.getTipoDescuento({ signal }),
  bases: (_, signal) => maestrosDataService.getBase({ signal }),
  grupo_descuentos: (_, signal) =>
    maestrosDataService.getGrupoDescuento({ signal }),
  beneficiarios: (_, signal) => maestrosDataService.getBeneficiario({ signal }),
  tipo_base: (_, signal) => maestrosDataService.getTipoBase({ signal }),
  tipo_rechazos: (_, signal) => maestrosDataService.getTipoRechazos({ signal }),
  estados_negociacion: (_, signal) =>
    maestrosDataService.getEstadosNegociacion({ signal }),
  periodos: (_, signal) =>
    maestrosDataService.getPeriodos({ signal }),
};

const MasterDataContext = createContext<MasterData | undefined>(undefined);

export const MasterDataProvider = ({
  children,
  requiredServices = [],
  serviceConfig = {},
}: {
  children: React.ReactNode;
  requiredServices?: string[];
  serviceConfig?: ServiceConfig;
}) => {
  const [data, setData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = async (signal?: AbortSignal) => {
    try {
      setLoading(true);

      const results = await Promise.all(
        requiredServices.map((key) => {
          const config = serviceConfig.hasOwnProperty(key)
            ? serviceConfig[key]
            : undefined;

          return serviceMap[key]?.(config, signal);
        })
      );

      const newData: Record<string, any> = {};
      requiredServices.forEach((key, index) => {
        const result = results[index];
        newData[key] = result?.data ?? result;
      });

      // Solo actualiza si los datos realmente cambiaron
      setData((prevData) => {
        const isEqual = JSON.stringify(prevData) === JSON.stringify(newData);
        return isEqual ? prevData : newData;
      });
    } catch (err) {
      if (!axios.isCancel(err)) {
        setError(err as Error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => {
      controller.abort();
    };
  }, [JSON.stringify(requiredServices), JSON.stringify(serviceConfig)]);

  const value: MasterData = {
    ...data,
    loading,
    error,
    refresh: () => {
      const controller = new AbortController();
      loadData(controller.signal);
    },
  };

  return (
    <MasterDataContext.Provider value={value}>
      {children}
    </MasterDataContext.Provider>
  );
};

export const useMasterData = () => {
  const context = useContext(MasterDataContext);
  if (!context) {
    throw new Error("useMasterData must be used within a MasterDataProvider");
  }
  return context;
};
